import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { staffApi, publicApi, errMsg } from "@/lib/api";
import { WEEKDAYS } from "@/lib/schedule";
import { PASTORAL } from "@/lib/pastoral";
import { TIMES } from "@/components/staff/categories";
import { useContent } from "@/lib/content-store";
import { Card } from "@/components/staff/ContentExtras";

const Chip = ({ on, onClick, children, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${on ? "border-[#0A3A6B] bg-[#0A3A6B] text-white" : "border-slate-200 bg-white text-slate-600 hover:border-[#1D64B4]"}`}>{children}</button>
);

const EMPTY = { title: "Sunday Worship Service", weekday: 6, weeks: [], time: "09:00", note: "", venue: "" };

const ScheduleRow = ({ s, onSaved, testId }) => {
  const [f, setF] = useState(s);
  const toggleWeek = (w) => setF({ ...f, weeks: f.weeks.includes(w) ? f.weeks.filter((x) => x !== w) : [...f.weeks, w].sort() });
  const save = async () => {
    const { id, ...body } = f;
    try { id ? await staffApi().put(`/worship-schedules/${id}`, body) : await staffApi().post("/worship-schedules", body); toast.success("Schedule saved"); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete "${f.title}" at ${f.time}?`)) return;
    try { await staffApi().delete(`/worship-schedules/${f.id}`); toast.success("Schedule deleted"); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-2 rounded-2xl bg-slate-50 p-3" data-testid={testId}>
      <div className="grid gap-2 sm:grid-cols-[1.4fr_130px_120px]">
        <Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Title" className="bg-white" data-testid={`${testId}-title`} />
        <Select value={String(f.weekday)} onValueChange={(v) => setF({ ...f, weekday: Number(v) })}>
          <SelectTrigger className="bg-white" data-testid={`${testId}-weekday`}><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">{WEEKDAYS.map((d, i) => <SelectItem key={d} value={String(i)}>{d}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={f.time} onValueChange={(v) => setF({ ...f, time: v })}>
          <SelectTrigger className="bg-white" data-testid={`${testId}-time`}><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-64 bg-white">{TIMES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        <Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="Language / note (e.g. Taglish)" className="bg-white" data-testid={`${testId}-note`} />
        <Input value={f.venue} onChange={(e) => setF({ ...f, venue: e.target.value })} placeholder="Venue (optional)" className="bg-white" data-testid={`${testId}-venue`} />
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="mr-1 text-xs text-slate-500">Weeks:</span>
        <Chip on={!f.weeks.length} onClick={() => setF({ ...f, weeks: [] })} testId={`${testId}-week-all`}>Every week</Chip>
        {[1, 2, 3, 4, 5].map((w) => <Chip key={w} on={f.weeks.includes(w)} onClick={() => toggleWeek(w)} testId={`${testId}-week-${w}`}>{["1st", "2nd", "3rd", "4th", "5th"][w - 1]}</Chip>)}
        <div className="ml-auto flex gap-1">
          <Button size="sm" onClick={save} className="h-8 rounded-full bg-[#0A3A6B] hover:bg-[#1D64B4]" data-testid={`${testId}-save`}><Save className="mr-1 h-3.5 w-3.5" />Save</Button>
          {f.id && <Button size="icon" variant="ghost" onClick={remove} className="h-8 w-8 text-rose-500" data-testid={`${testId}-delete`}><Trash2 className="h-4 w-4" /></Button>}
        </div>
      </div>
    </div>
  );
};

const WorshipEditor = () => {
  const content = useContent();
  const [adding, setAdding] = useState(false);
  const saved = () => { setAdding(false); content.reload(); };
  return (
    <Card title="Worship service schedules" hint="Shown on the Worship Services page, in forms and on the staff calendar.">
      <Button variant="outline" size="sm" onClick={() => setAdding(true)} data-testid="worship-add-button"><Plus className="mr-1 h-4 w-4" /> Add schedule</Button>
      {adding && <ScheduleRow s={EMPTY} onSaved={saved} testId="worship-new" />}
      {(content.schedules || []).map((s) => <ScheduleRow key={`${s.id}-${s.time}-${s.weekday}`} s={s} onSaved={saved} testId={`worship-row-${s.id}`} />)}
    </Card>
  );
};

const MinisterRow = ({ m, save, remove }) => {
  const [email, setEmail] = useState(m.email || "");
  const [day, setDay] = useState("");
  const days = m.days_off || [];
  const addDay = () => { if (day && !days.includes(day)) save({ ...m, days_off: [...days, day].sort() }); setDay(""); };
  return (
    <div className="space-y-2 rounded-2xl bg-slate-50 p-3" data-testid={`minister-row-${m.id}`}>
      <div className="flex items-center justify-between"><p className="font-medium text-slate-900">{m.name}</p>
        <Button size="icon" variant="ghost" onClick={() => remove(m)} className="h-8 w-8 text-rose-500" data-testid={`minister-delete-${m.id}`}><Trash2 className="h-4 w-4" /></Button></div>
      <div className="flex gap-2">
        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email for notifications" className="h-9 bg-white" data-testid={`minister-email-${m.id}`} />
        <Button size="sm" variant="outline" disabled={email === (m.email || "")} onClick={() => save({ ...m, email: email.trim() })} data-testid={`minister-email-save-${m.id}`}>Save</Button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {Object.entries(PASTORAL).map(([k, p]) => (
          <Chip key={k} on={m.services.includes(k)} testId={`minister-${m.id}-${k}`}
            onClick={() => save({ ...m, services: m.services.includes(k) ? m.services.filter((x) => x !== k) : [...m.services, k] })}>{p.title.split(" |")[0]}</Chip>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-xs text-slate-500">Days off:</span>
        {days.map((d) => <Chip key={d} on testId={`minister-dayoff-${m.id}-${d}`} onClick={() => save({ ...m, days_off: days.filter((x) => x !== d) })}>{d} ×</Chip>)}
        <input type="date" value={day} onChange={(e) => setDay(e.target.value)} className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-xs" data-testid={`minister-dayoff-input-${m.id}`} />
        <Button size="sm" variant="outline" className="h-8" disabled={!day} onClick={addDay} data-testid={`minister-dayoff-add-${m.id}`}>Add</Button>
      </div>
    </div>
  );
};

const MinistersEditor = () => {
  const [list, setList] = useState([]);
  const [name, setName] = useState("");
  const load = useCallback(() => { publicApi.get("/public/ministers").then((r) => setList(r.data)); }, []);
  useEffect(() => { load(); }, [load]);
  const update = async (m) => {
    const { id, ...body } = m;
    try { await staffApi().put(`/ministers/${id}`, body); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const add = async (e) => {
    e.preventDefault();
    try { await staffApi().post("/ministers", { name, services: Object.keys(PASTORAL) }); setName(""); toast.success("Minister added"); load(); } catch (err) { toast.error(errMsg(err)); }
  };
  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.name}?`)) return;
    try { await staffApi().delete(`/ministers/${m.id}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card title="Officiating ministers" hint="Set each minister's email, the services they can officiate, and days off (used for availability checks).">
      <form onSubmit={add} className="flex gap-2"><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Minister's full name" data-testid="minister-name-input" /><Button type="submit" disabled={!name.trim()} className="rounded-full bg-[#0A3A6B] hover:bg-[#1D64B4]" data-testid="minister-add-button"><Plus className="h-4 w-4" /></Button></form>
      {list.map((m) => <MinisterRow key={`${m.id}-${m.email}`} m={m} save={update} remove={remove} />)}
    </Card>
  );
};

const BlockedDays = () => {
  const [days, setDays] = useState([]);
  useEffect(() => { publicApi.get("/public/pastoral-settings").then((r) => setDays(r.data.blocked_weekdays)); }, []);
  const save = async () => {
    try { await staffApi().put("/pastoral-settings", { blocked_weekdays: days }); toast.success("Blocked days saved"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card title="Blocked days for pastoral services" hint="Visitors can't pick these days in pastoral care forms.">
      <div className="flex flex-wrap gap-1.5">
        {WEEKDAYS.map((d, i) => <Chip key={d} on={days.includes(i)} testId={`blocked-day-${i}`} onClick={() => setDays(days.includes(i) ? days.filter((x) => x !== i) : [...days, i])}>{d}</Chip>)}
      </div>
      <Button size="sm" onClick={save} className="rounded-full bg-[#0A3A6B] hover:bg-[#1D64B4]" data-testid="blocked-days-save"><Save className="mr-1 h-4 w-4" /> Save</Button>
    </Card>
  );
};

export const AdminPastoral = () => (
  <div className="space-y-6" data-testid="admin-pastoral">
    <div>
      <h2 className="text-2xl font-bold tracking-tight text-slate-900">Pastoral & Schedules</h2>
      <p className="text-sm text-slate-500">Worship schedules, officiating ministers and blocked days.</p>
    </div>
    <div className="grid gap-6 lg:grid-cols-2">
      <WorshipEditor />
      <div className="space-y-6"><MinistersEditor /><BlockedDays /></div>
    </div>
  </div>
);
